import isNil from "lodash/isNil";
import { Suspense, lazy } from "react";
import PropTypes from "prop-types";
import LoadingFallback from "../../LoadingFallback";
import RffAddressEditingField from "../inputs/RffAddressEditingField";
import RffBasicHtmlEditor from "../inputs/RffBasicHtmlEditor";

// Lazy load ALL form inputs for maximum bundle savings
const RffBooleanCheckbox = lazy(() => import("../inputs/RffBooleanCheckbox"));
const RffBooleanToggleField = lazy(
  () => import("../inputs/RffBooleanToggleField"),
);
const RffDateField = lazy(() => import("../inputs/RffDateField"));
const RffDollarField = lazy(() => import("../inputs/RffDollarField"));
const RffEmailField = lazy(() => import("../inputs/RffEmailField"));
const RffNumberField = lazy(() => import("../inputs/RffNumberField"));
const RffPercentField = lazy(() => import("../inputs/RffPercentField"));
const RffPhoneField = lazy(() => import("../inputs/RffPhoneField"));
const RffSelectField = lazy(() => import("../inputs/RffSelectField"));
const RffSelectToggleField = lazy(
  () => import("../inputs/RffSelectToggleField"),
);
const RffSsnField = lazy(() => import("../inputs/RffSsnField"));
const RffTextField = lazy(() => import("../inputs/RffTextField"));
const RffDisplayField = lazy(() => import("../inputs/RffDisplayField"));
const RffFloatField = lazy(() => import("../inputs/RffFloatField"));
const RffSelectMultipleField = lazy(
  () => import("../inputs/RffSelectMultipleField"),
);
const RffSelectAutocompleteField = lazy(
  () => import("../inputs/RffSelectAutocompleteField"),
);
const RffSelectUserEmailField = lazy(
  () => import("../inputs/RffSelectUserEmailField"),
);
const RffSelectMultipleUserEmailField = lazy(
  () => import("../inputs/RffSelectMultipleUserEmailField"),
);
const RffAddressGeolocateField = lazy(
  () => import("../inputs/RffAddressGeolocateField"),
);
const RffAddressGeolocateInputField = lazy(
  () => import("../inputs/RffAddressGeolocateInputField"),
);
const RffTextareaField = lazy(() => import("../inputs/RffTextareaField"));

// Component map defined outside to avoid recreation
const componentMap = {
  default: RffTextField,
  text: RffTextField,
  dollar: RffDollarField,
  percent: RffPercentField,
  phone: RffPhoneField,
  email: RffEmailField,
  ssn: RffSsnField,
  booleanCheckbox: RffBooleanCheckbox,
  booleanToggle: RffBooleanToggleField,
  select: RffSelectField,
  selectToggle: RffSelectToggleField,
  selectMultiple: RffSelectMultipleField,
  selectMultipleUserEmail: RffSelectMultipleUserEmailField,
  selectAutocomplete: RffSelectAutocompleteField,
  selectUserEmail: RffSelectUserEmailField,
  number: RffNumberField,
  integer: RffNumberField,
  float: RffFloatField,
  date: RffDateField,
  html: RffBasicHtmlEditor,
  stringMultiline: RffTextareaField,
  textarea: RffTextareaField,
  address: RffAddressGeolocateField,
  addressInput: RffAddressGeolocateInputField,
  addressEditable: RffAddressEditingField,
  string: RffTextField,
  display: RffDisplayField,
};

const RffFieldComponentRenderer = ({ field }) => {
  const componentFieldName = field.field || field.name;
  const componentFieldType =
    field.type || field.inputType || field.formType || field.form_type;
  if (
    isNil(componentFieldName) ||
    componentFieldName === " " ||
    componentFieldName === ""
  ) {
    return null;
  }

  const componentKey = componentFieldType;
  const Component = componentMap[componentKey] || componentMap.default;

  // Extract the key prop and create props object without it
  const { key, ...componentProps } = {
    key: field.id,
    name: componentFieldName,
    label: field.label,
    size: field.size || 12,
    required: field.required,
    validate: field.validate,
    helperText: field.helperText,
    options: field.choices || field.options,
    elements: field?.elements,
    field: field,
    suppressGrid: field?.suppressGrid,
    fullWidth: field?.fullWidth,
    minHeight: field?.minHeight,
    placeholder: field?.placeholder,
    disabled: field?.disabled,
    onBlur: field?.onBlur,
    toggleSize: field.toggleSize,
    parameters: field.parameters,
  };

  return (
    <Suspense fallback={<LoadingFallback height={18} />}>
      <Component key={key} {...componentProps} />
    </Suspense>
  );
};

RffFieldComponentRenderer.propTypes = {
  field: PropTypes.shape({
    field: PropTypes.string,
    inputType: PropTypes.string,
    type: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    size: PropTypes.number,
    required: PropTypes.bool,
    validate: PropTypes.func,
    helperText: PropTypes.string,
    choices: PropTypes.array,
    options: PropTypes.array,
    elements: PropTypes.any,
    suppressGrid: PropTypes.bool,
    fullWidth: PropTypes.bool,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    onBlur: PropTypes.func,
    toggleSize: PropTypes.number,
    parameters: PropTypes.object,
  }).isRequired,
};

export default RffFieldComponentRenderer;
